/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useRef, useState} from "react";

import {useDispatch} from "react-redux";
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";

import ApplicationApiContext from "@Contexts/Api";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";

import USER_INTERFACE from "@Services/Application/Interfaces/Users/User";

import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";


import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";


import dateFormat from "dateformat";


import {Redirect} from "react-router-dom";


import {Col, Row} from "react-bootstrap";


import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";


import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";


import SettingsSMSCodeVerificationComponent from "@UIPages/Authenticated/Settings/_Parts/Verification";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";
import CHANGE_API_REQUEST_TOKEN_ACTION from "@Actions/Api/ChangeApiRequestTokenValue";
import prepareRestApiRequestToken from "@Services/Utilities/RequestToken/Prepare";


type MyProfilePageProps = {};


export const SettingsMyProfilePageComponent: React.FC<MyProfilePageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    const [USER_AUTHENTICATED_STATUS, SET_USER_AUTHENTICATED_STATUS] = useState<boolean>(false);

    const CHANGE_USER_AUTHENTICATED_STATUS = (response: any) => {
        if (response.OperationGUID) SET_USER_AUTHENTICATED_STATUS(true);
    }

    /** @var {[object, function]} State - User Profile Data Object, User Profile Data Object Setter */
    const [USER_PROFILE_DATA, SET_USER_PROFILE_DATA] = useState<USER_INTERFACE | null>(null);
    /** @var {[boolean, function]} State - User Profile Component Error, User Profile Component Error Setter */
    const [USER_PROFILE_ERROR, SET_USER_PROFILE_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);


    /** @hook {didMount, didUpdate} Get Terminals List from REST API */
    useEffect(() => {

        if (!USER_AUTHENTICATED_STATUS) return;

        /* Check if Monitory Note ID, API URL Present, User Logged */
        if (!REST_API_URL || !LOGGED_USER?.userID) return; // No Valid Data - Stop

        /** @const USER_PROFILE_API_URL User Profile REST API Endpoint URL */
        const USER_PROFILE_API_URL: string = REST_API_URL + "/User/Get";

        /** @const USER_PROFILE_REQUEST_DATA User Profile Request Data Object */
        const USER_PROFILE_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID
            ContentType: "G" // Data Return Mode - Main User Data
        };

        /** @async {function} User Profile Data Request Handler */
        const GET_USER_PROFILE_DATA = async (): Promise<void | Function> => {
            /* Change Global Rest Api Request Status Action */
            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true)); // Set Status to true

            try {

                const USER_DATA_RESPONSE = await REST_API_REQUEST(
                    USER_PROFILE_API_URL,
                    USER_PROFILE_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (USER_DATA_RESPONSE.status === 200) {

                    const CONTENT = await USER_DATA_RESPONSE.text();
                    if (CONTENT.trim()) {
                        SET_USER_PROFILE_DATA(JSON.parse(CONTENT));
                    }

                } else {
                    if (USER_DATA_RESPONSE.status === 409) {
                        const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await USER_DATA_RESPONSE.json();
                        SET_USER_PROFILE_ERROR({ErrorCode: CONTENT.ErrorCode, UserMessage: CONTENT.UserMessage});
                    } else {
                        SET_USER_PROFILE_ERROR({ErrorCode: USER_DATA_RESPONSE.status, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
                    }
                }

            } catch (e) {
                SET_USER_PROFILE_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

            /* Change Global Rest Api Request Status Action */
            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

        };

        GET_USER_PROFILE_DATA();

    }, [USER_AUTHENTICATED_STATUS]);


    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={ROUTER.NOT_AUTHENTICATED.LOGIN.URL}/>;


    if (!ROUTER) return <Redirect to={"/404"}/>;


    if (!!USER_PROFILE_ERROR) return <ErrorMessageComponent messageText={USER_PROFILE_ERROR.UserMessage} />;


    if (!USER_AUTHENTICATED_STATUS) return <SettingsSMSCodeVerificationComponent verificationStatusHandler={CHANGE_USER_AUTHENTICATED_STATUS} />;


    if (!USER_PROFILE_DATA) return null;


    const {
        Login: USER_LOGIN, Driver: USER_DRIVER_DATA,
    } = USER_PROFILE_DATA;


    if (!USER_DRIVER_DATA) return null;


    const UserCardsList = USER_DRIVER_DATA[0] && USER_DRIVER_DATA[0]?.Cards?.length ? USER_DRIVER_DATA[0]?.Cards.map(
        (card: any, i: number) => {
            return (
                <Col key={i} xs={12} className={"user-single-card-information-component user-single-card-information user-card-component user-card"}>
                    <span className={"card-number"}>{card?.CardNo}</span>
                    {!!card?.ExpireDate &&
	                <span className={"card-number"}>{`ważna do`} {dateFormat(card?.ExpireDate, "dd-mm-yyyy")}</span>
                    }
                    <span className={"card-active-status"}>({card?.Active ? "aktywna" : "nieaktywna"})</span>
                </Col>
            );
        }
    ) : "Brak";

    const UserOperatorsList = USER_DRIVER_DATA[0] && USER_DRIVER_DATA[0]?.Applicants?.length ? USER_DRIVER_DATA[0]?.Applicants.map(
        (operator: any, i: number) => {
            return (
                <Col key={i} xs={12} className={"user-single-card-information-component user-single-card-information user-operator-component user-operator"}>
                    <span>{operator?.fdName || operator?.ShortName}</span>
                </Col>
            );
        }
    ) : "Brak";


    return (
        <BlockContentContainerComponent forwardRef={COMPONENT_REF} cssClasses={"settings-page-user-data-component settings-page-view-component"}>

            <BlockContentHeaderComponent headerText={"Moje Dane"} />

            <Col xs={12} className={"user-profile-data user-information-data"}>
                <Row className={"user-profile-data-content"}>

                    <Col xs={12} className={"user-information-section-component user-information-section user-profile-main-data user-profile-data user-main-data"}>
                        <Row className={"user-information-section-content"}>
                            <Col xs={12} className={"user-information-data user-important-information user-name"}>
                                <span>{USER_DRIVER_DATA[0]?.Firstname} {USER_DRIVER_DATA[0]?.Surname}</span>
                            </Col>
                            <Col xs={12} className={"user-information-data user-login"}>
                                <span className={"information-label"}>{`login`}:</span>
                                <span className={"information-value"}>{USER_LOGIN}</span>
                            </Col>
                            <Col xs={12} className={"user-information-data user-full-name"}>
                                <span className={"information-label"}>{`kraj`}:</span>
                                <span className={"information-value"}>{USER_DRIVER_DATA[0]?.Country}</span>
                            </Col>
                            <Col xs={12} className={"user-information-data user-birthdate"}>
                                <span className={"information-label"}>{`data urodzenia`}:</span>
                                <span className={"information-value"}>{dateFormat(USER_DRIVER_DATA[0]?.Birthdate, "dd-mm-yyyy")}</span>
                            </Col>
                            <Col xs={12} className={"user-information-data user-social-id"}>
                                <span className={"information-label"}>{`pesel`}:</span>
                                <span className={"information-value"}>{USER_DRIVER_DATA[0]?.PESEL}</span>
                            </Col>
                            <Col xs={12} className={"user-information-data user-phone-nr"}>
                                <span className={"information-label"}>{`telefon`}:</span>
                                <span className={"information-value"}>{USER_DRIVER_DATA[0]?.Phone}</span>
                            </Col>
                        </Row>
                    </Col>

                    {UserCardsList.length > 0 &&
                    <Col xs={12} className={"user-information-section-component user-information-section user-profile-cards-data user-profile-data"}>
                        <Row className={"user-information-section-content"}>
                            <Col xs={12} className={"user-information-section-header"}>
                                <span>{`Karty`}</span>
                            </Col>
                            {UserCardsList}
                        </Row>
                    </Col>
                    }

                    {UserOperatorsList.length > 0 &&
                    <Col xs={12} className={"user-information-section-component user-information-section user-profile-operators-data user-profile-data"}>
                        <Row className={"user-information-section-content"}>
                            <Col xs={12} className={"user-information-section-header"}>
                                <span>{`Zgłaszający`}</span>
                            </Col>
                            {UserOperatorsList}
                        </Row>
                    </Col>
                    }

                </Row>
            </Col>

        </BlockContentContainerComponent>
    );

}


export default SettingsMyProfilePageComponent;
